import {el, marketingTag} from "./progress"

const ENHANCED = {
  "5.1.1": {
    title: "User unknown",
    meaning: "The address does not exist.",
    action: "It is suppressed as a hard bounce. Remove it."
  },
  "5.1.10": {
    title: "Recipient rejected",
    meaning: "The domain publishes a null MX, or the recipient address was rejected.",
    action: "Remove the address. A null MX means this domain does not accept mail."
  },
  "5.2.1": {
    title: "Mailbox disabled",
    meaning: "The mailbox exists but is disabled.",
    action: "Treat it as a hard bounce and stop sending."
  },
  "4.2.2": {
    title: "Mailbox full",
    meaning: "The recipient's inbox is out of storage space.",
    action: "This is temporary. Retry later, or ask the recipient to free space."
  },
  "5.2.2": {
    title: "Mailbox full",
    meaning: "The recipient's inbox is out of storage space.",
    action: "The provider marked this permanent. Suppress the address for now."
  },
  "5.4.1": {
    title: "Recipient rejected",
    meaning: "Relay access was denied, or the recipient was rejected.",
    action: "Check the address and whether you are allowed to send to that host."
  },
  "4.7.0": {
    title: "Rate limited",
    meaning: "A Gmail-style provider deferred this for rate or authentication.",
    action: "Slow down. Check SPF, DKIM, and DMARC alignment."
  },
  "4.7.28": {
    title: "Rate limited",
    meaning: "A Gmail-style provider deferred this for rate or authentication.",
    action: "Slow down. Check SPF, DKIM, and DMARC alignment."
  },
  "4.7.26": {
    title: "Rate limited",
    meaning: "A Gmail-style provider deferred this for rate or authentication.",
    action: "Slow down. Check SPF, DKIM, and DMARC alignment."
  },
  "5.7.1": {
    title: "Rejected by policy",
    meaning: "The provider refused the message, often for content or reputation.",
    action: "Check blocklists and the stream that sent this."
  },
  "5.7.26": {
    title: "Unauthenticated",
    meaning: "DMARC, SPF, or DKIM failed.",
    action: "Check SPF, DKIM, and DMARC alignment.",
    href: "/learn/dns-setup"
  },
  "5.7.509": {
    title: "Access denied",
    meaning: "Outlook denied the message on reputation.",
    action: "Hold that provider's share and repair the stream before you grow."
  },
  "5.7.708": {
    title: "Access denied",
    meaning: "Outlook denied the message on reputation.",
    action: "Hold that provider's share and repair the stream before you grow."
  },
  "4.3.2": {
    title: "Too many connections",
    meaning: "The receiving host asked you to try later.",
    action: "This is a deferral. Wait, then send again with fewer connections."
  },
  "4.4.1": {
    title: "Connection timed out",
    meaning: "The connection to the receiving host timed out.",
    action: "Retry later. If it keeps happening, check the destination host."
  },
  "4.4.2": {
    title: "Connection timed out",
    meaning: "The connection to the receiving host timed out.",
    action: "Retry later. If it keeps happening, check the destination host."
  }
}

const BASIC = {
  "421": {
    title: "Try later",
    meaning: "The receiving host asked you to try later.",
    action: "This is a deferral. Wait, then send again."
  }
}

const CLASSES = {
  "4": {
    tag: "Temporary",
    tone: "yellow",
    title: "Temporary failure",
    meaning: "The receiving server asked you to try later.",
    action: "Wait, then send again. Do not treat this as a hard bounce."
  },
  "5": {
    tag: "Permanent",
    tone: "red",
    title: "Permanent failure",
    meaning: "The receiving server rejected this message.",
    action: "Do not keep retrying this address until the cause is fixed."
  }
}

function parseReply(text) {
  const source = String(text || "")
  const enhanced = source.match(/\b(\d\.\d{1,3}\.\d{1,3})\b/)
  const basic = source.match(/\b([245]\d{2})\b/)
  return {
    basic: basic ? basic[1] : "",
    enhanced: enhanced ? enhanced[1] : ""
  }
}

function lookup(parsed) {
  const enhanced = ENHANCED[parsed.enhanced]
  const basic = BASIC[parsed.basic]
  const klass = CLASSES[(parsed.enhanced || parsed.basic || "").charAt(0)] || CLASSES["5"]
  const found = enhanced || basic || klass
  const classKey = (parsed.enhanced || parsed.basic || "5").charAt(0)
  return {
    tag: classKey === "4" ? "Temporary" : "Permanent",
    tone: classKey === "4" ? "yellow" : "red",
    title: found.title,
    meaning: found.meaning,
    action: found.action,
    href: found.href || ""
  }
}

function codesLabel(parsed) {
  return [parsed.basic, parsed.enhanced].filter(Boolean).join(" ") || "No code found"
}

function setAction(node, decoded) {
  node.replaceChildren()
  if (!decoded.href) {
    node.textContent = decoded.action
    return
  }
  node.append(document.createTextNode(`${decoded.action} `))
  const link = el("a", "text-ink underline decoration-line-strong underline-offset-4 hover:decoration-ink", "Read DNS setup")
  link.href = decoded.href
  node.append(link)
}

function render(root) {
  const text = root.querySelector("[data-learn-reply]")?.value || ""
  const parsed = parseReply(text)
  const decoded = lookup(parsed)
  const codes = root.querySelector("[data-learn-codes]")
  if (codes) codes.textContent = codesLabel(parsed)
  const tag = root.querySelector("[data-learn-tag]")
  if (tag) tag.replaceChildren(marketingTag(decoded.tag, decoded.tone, true))
  const title = root.querySelector("[data-learn-title]")
  if (title) title.textContent = decoded.title
  const meaning = root.querySelector("[data-learn-meaning]")
  if (meaning) meaning.textContent = decoded.meaning
  const action = root.querySelector("[data-learn-action]")
  if (action) setAction(action, decoded)
}

function selectSample(root, button) {
  root.querySelectorAll("[data-learn-sample]").forEach((other) => {
    other.setAttribute("aria-pressed", String(other === button))
  })
  const input = root.querySelector("[data-learn-reply]")
  if (input) input.value = button.dataset.learnSample || ""
  render(root)
}

export function mount(root) {
  root.querySelectorAll("[data-learn-sample]").forEach((button) => {
    button.addEventListener("click", () => selectSample(root, button))
  })
  root.querySelector("[data-learn-reply]")?.addEventListener("input", () => {
    root.querySelectorAll("[data-learn-sample]").forEach((button) => {
      const match = button.dataset.learnSample === root.querySelector("[data-learn-reply]")?.value
      button.setAttribute("aria-pressed", String(match))
    })
    render(root)
  })
  render(root)
}
